import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AIAnalysisPanel from "@/pages/psy/AIAnalysisPanel";

/** Cores/labels para os 5 níveis (Sem dados | Baixo | Moderado | Alto | Crítico)
 *  alinhados com psy_tests_library.classify_risk. */
const LEVEL_BG = {
  0: { bg: "bg-slate-300",  text: "text-slate-600",  light: "bg-slate-50" },
  1: { bg: "bg-emerald-500", text: "text-emerald-900", light: "bg-emerald-50" },
  2: { bg: "bg-amber-500",   text: "text-amber-900",   light: "bg-amber-50" },
  3: { bg: "bg-orange-500",  text: "text-orange-900",  light: "bg-orange-50" },
  4: { bg: "bg-rose-600",    text: "text-rose-900",    light: "bg-rose-50" },
};

const INSTR_VISUAL = {
  rapido: { icon: "fa-bolt-lightning", color: "emerald" },
  copsoq: { icon: "fa-magnifying-glass-chart", color: "indigo" },
  hse:    { icon: "fa-shield-halved", color: "amber" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

export default function CompanyPsyAggregate() {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/psy/companies/${companyId}/aggregate`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar análise consolidada"))
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) {
    return (
      <div className="text-slate-500 p-8" data-testid="company-psy-aggregate-loading">
        <i className="fa-solid fa-spinner fa-spin mr-2"></i>Consolidando avaliações da empresa…
      </div>
    );
  }

  if (!data) return null;

  const sc = data.scoring;
  const overallCls = LEVEL_BG[sc.overall.level] || LEVEL_BG[0];
  const hasResponses = sc.response_count > 0;

  return (
    <div className="space-y-6" data-testid="company-psy-aggregate">
      {/* Header */}
      <header className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <Link
          to="/app/testes-psicossociais"
          className="text-sm text-slate-500 hover:text-slate-800 mb-3 inline-block"
          data-testid="back-to-test-center"
        >
          <i className="fa-solid fa-arrow-left mr-1"></i> Voltar para Testes Psicossociais
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">
              <i className="fa-solid fa-layer-group mr-1"></i>Análise Psicossocial Consolidada
            </div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="company-psy-aggregate-title">
              {data.company.razao_social || data.company.nome_fantasia}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              <i className="fa-solid fa-id-card mr-1"></i>
              {data.company.cnpj || "Sem CNPJ"} · {data.campaign_count} campanha(s) ·{" "}
              {sc.response_count} resposta(s) consolidadas
            </p>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-4 leading-relaxed max-w-3xl">
          Esta visão unifica todas as avaliações psicossociais aplicadas nesta empresa
          (Rápido, COPSOQ, HSE), normalizando os resultados nas <strong>9 dimensões NR-01</strong> para
          permitir uma leitura executiva única. Cada item do plano de ação é gerado
          automaticamente a partir do nível de risco consolidado da respectiva dimensão.
        </p>
      </header>

      {!hasResponses ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="company-psy-aggregate-empty">
          <i className="fa-solid fa-folder-open text-4xl text-slate-300 mb-3"></i>
          <h2 className="text-lg font-bold text-slate-800 mb-1">Sem respostas consolidáveis ainda</h2>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Crie campanhas e colete respostas para visualizar a análise unificada da empresa.
          </p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Campanhas</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="kpi-campaign-count">
                {data.campaign_count}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {data.by_instrument.length} instrumento(s) diferente(s)
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Respostas</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="kpi-response-count">
                {sc.response_count}
              </div>
              {data.participacao_pct !== null && data.participacao_pct !== undefined && (
                <div className="text-xs text-slate-500 mt-1">{data.participacao_pct}% de participação</div>
              )}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Score consolidado</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="kpi-overall-score">
                {sc.overall_score.toFixed(2)}
              </div>
              <div className={`text-xs font-bold mt-1 inline-block px-2 py-0.5 rounded ${overallCls.light} ${overallCls.text}`}>
                {sc.overall.label}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Críticos</div>
              <div className="text-3xl font-bold text-rose-700 mt-1" data-testid="kpi-critical-count">
                {sc.critical_count}
              </div>
              <div className="text-xs text-slate-500 mt-1">marcações de risco máximo</div>
            </div>
          </section>

          {/* Instrumentos usados */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-4">
              <i className="fa-solid fa-toolbox mr-2 text-slate-400"></i>Instrumentos aplicados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="instruments-grid">
              {data.by_instrument.map((b) => {
                const v = INSTR_VISUAL[b.test_id] || INSTR_VISUAL.rapido;
                return (
                  <div
                    key={b.test_id}
                    className={`border border-${v.color}-200 bg-${v.color}-50 rounded-xl p-4`}
                    data-testid={`instrument-card-${b.test_id}`}
                  >
                    <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-${v.color}-600 text-white mb-2`}>
                      <i className={`fa-solid ${v.icon}`}></i>
                    </div>
                    <div className="font-semibold text-slate-900 text-sm leading-tight">{b.test_name}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      {b.campaign_count} campanha(s) · {b.response_count} resposta(s)
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Score por dimensão (9 dimensões legadas) */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-4">
              <i className="fa-solid fa-chart-column mr-2 text-slate-400"></i>
              Score consolidado por dimensão (9 NR-01)
            </h2>
            <div className="space-y-3">
              {sc.dimensions.map((d) => {
                const cls = LEVEL_BG[d.level] || LEVEL_BG[0];
                const widthPct = d.responses > 0 ? (d.score / 5) * 100 : 0;
                return (
                  <div key={d.id} data-testid={`dim-row-${d.id}`}>
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="text-sm font-semibold text-slate-800">{d.name}</div>
                      <div className="text-xs flex items-baseline gap-2">
                        <span className="font-mono font-bold text-slate-900">
                          {d.responses > 0 ? d.score.toFixed(2) : "—"}
                        </span>
                        <span className={`font-bold px-2 py-0.5 rounded ${cls.light} ${cls.text}`}>
                          {d.label}
                        </span>
                      </div>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${cls.bg} rounded-full transition-all duration-500`}
                        style={{ width: `${widthPct}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.responses} resposta(s) consolidada(s)
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Matriz de risco */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <header className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-900">
                <i className="fa-solid fa-grid-2 mr-2 text-slate-400"></i>Matriz de risco consolidada
              </h2>
            </header>
            {data.matriz_risco.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Sem dados suficientes ainda.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="company-psy-risk-matrix">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Fator psicossocial</th>
                      <th className="px-4 py-3 text-center">Média</th>
                      <th className="px-4 py-3 text-center">Severidade</th>
                      <th className="px-4 py-3 text-center">Probab.</th>
                      <th className="px-4 py-3 text-left">Nível</th>
                      <th className="px-4 py-3 text-left">Prioridade</th>
                      <th className="px-4 py-3 text-center">Respostas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.matriz_risco.map((m, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 font-semibold text-slate-800">
                          {m.fator}
                          <div className="text-xs font-normal text-slate-500">{m.descricao}</div>
                        </td>
                        <td className="px-4 py-3 text-center font-mono font-bold">{m.media.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center">{m.severidade}</td>
                        <td className="px-4 py-3 text-center">{m.probabilidade}</td>
                        <td className="px-4 py-3">
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white"
                            style={{ backgroundColor: m.color }}
                          >
                            {m.nivel}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-700">{m.prioridade}</td>
                        <td className="px-4 py-3 text-center text-slate-500">{m.responses}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Plano de ação consolidado */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <header className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-wrap gap-2">
              <h2 className="font-bold text-slate-900">
                <i className="fa-solid fa-list-check mr-2 text-slate-400"></i>Plano de ação consolidado
              </h2>
              <span className="text-xs text-slate-500">
                Gerado a partir do nível de risco consolidado de cada dimensão.
              </span>
            </header>
            {data.plano_acao.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                <i className="fa-solid fa-check-double text-3xl block mb-3 text-emerald-500"></i>
                Nenhuma dimensão atingiu nível classificável. Mantenha o monitoramento.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="company-psy-action-plan">
                {data.plano_acao.map((p) => (
                  <li key={p.id} className="p-5">
                    <div className="flex flex-wrap items-start gap-3 mb-2">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white"
                        style={{ backgroundColor: p.color }}
                      >
                        {p.nivel}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{p.tipo}</span>
                      <span className="text-xs font-semibold text-slate-700">Prioridade: {p.prioridade}</span>
                      <h3 className="font-bold text-slate-900 w-full">
                        {p.fator}{" "}
                        <span className="text-slate-400 font-normal">— média {p.media.toFixed(2)}</span>
                      </h3>
                    </div>
                    <p className="text-sm text-slate-700 mt-1"><b>Problema:</b> {p.problema}</p>
                    <p className="text-sm text-slate-700 mt-1"><b>Evidência:</b> {p.evidencia}</p>
                    <p className="text-sm text-slate-700 mt-1"><b>Causa provável:</b> {p.causa}</p>
                    <p className="text-sm text-slate-700 mt-1"><b>Ação recomendada:</b> {p.acao}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                      <div><span className="text-slate-500 block">Responsável</span><span className="font-medium text-slate-800">{p.responsavel}</span></div>
                      <div><span className="text-slate-500 block">Prazo</span><span className="font-medium text-slate-800">{p.prazo}</span></div>
                      <div><span className="text-slate-500 block">Indicador</span><span className="font-medium text-slate-800">{p.indicador}</span></div>
                      <div><span className="text-slate-500 block">Verificação</span><span className="font-medium text-slate-800">{p.verificacao}</span></div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {/* AI Analysis Panel (consolidated) */}
      {hasResponses && (
        <AIAnalysisPanel
          scope="company"
          companyId={companyId}
          responseCount={sc.response_count}
        />
      )}

      {/* Campanhas que compõem a análise */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">
            <i className="fa-solid fa-folder-tree mr-2 text-slate-400"></i>
            Campanhas que compõem esta análise
          </h2>
        </header>
        {data.campaigns.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nenhuma campanha cadastrada ainda.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="company-psy-campaigns-included">
            {data.campaigns.map((c) => {
              const v = INSTR_VISUAL[c.test_id] || INSTR_VISUAL.rapido;
              return (
                <li key={c.id} className="px-6 py-3 hover:bg-slate-50 flex items-center gap-4">
                  <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-${v.color}-600 text-white shrink-0`}>
                    <i className={`fa-solid ${v.icon}`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/app/testes-psicossociais/${c.id}`}
                      className="font-semibold text-slate-900 hover:text-emerald-700 truncate block"
                      data-testid={`included-campaign-${c.id}`}
                    >
                      {c.titulo}
                    </Link>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><i className="fa-solid fa-tag mr-1"></i>{c.test_name}</span>
                      {c.setor_ghe && <span><i className="fa-solid fa-sitemap mr-1"></i>{c.setor_ghe}</span>}
                      <span><i className="fa-solid fa-users mr-1"></i>{c.response_count} resp.</span>
                      <span><i className="fa-regular fa-calendar mr-1"></i>{fmtDate(c.created_at)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <footer className="text-xs text-slate-400 text-center py-4">
        <i className="fa-solid fa-shield-halved mr-1"></i>
        Análise consolidada da empresa para fins gerenciais. Não substitui avaliação clínica
        individual nem diagnóstico médico.
      </footer>
    </div>
  );
}
